import React, { useState, useEffect } from "react";
import { X, ExternalLink, ChevronDown } from "lucide-react";
import axios from "axios";
import Cookies from "js-cookie";

const API_BASE_URL = import.meta.env.VITE_API_URL || "http://localhost:6002/api";
const BRAND_COLOR = import.meta.env.VITE_BRAND_COLOR || "#153d23";

const axiosInstance = axios.create({ baseURL: API_BASE_URL });
axiosInstance.interceptors.request.use((config) => {
  const token = Cookies.get("crm_auth_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

interface AgentRow {
  user_id: number;
  agent_name: string;
  lead_count: number;
}

interface Lead {
  lead_id: number;
  uuid: string;
  name: string;
  phone: string;
  created_date: string;
}

interface DispositionBreakdownProps {
  fromDate: string;
  toDate: string;
  leadBucket: string | null;
  l1: number | null;
  l2: number | null;
  l3: number | null;
  lastAbcDateFrom?: string | null;
  lastAbcDateTo?: string | null;
  lastCallStatus?: string | null;
  lastCallDurationMin?: number | null;
}

function maskPhone(phone: string): string {
  if (!phone) return "—";
  if (phone.length <= 4) return phone;
  return "****" + phone.slice(-4);
}

function initials(name: string): string {
  return name
    .split(" ")
    .slice(0, 2)
    .map((n) => n[0]?.toUpperCase() || "")
    .join("");
}

export const DispositionBreakdown: React.FC<DispositionBreakdownProps> = ({
  fromDate,
  toDate,
  leadBucket,
  l1,
  l2,
  l3,
  lastAbcDateFrom,
  lastAbcDateTo,
  lastCallStatus,
  lastCallDurationMin,
}) => {
  const [agents, setAgents] = useState<AgentRow[]>([]);
  const [totalLeads, setTotalLeads] = useState(0);
  const [dispositionLabel, setDispositionLabel] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Modal state
  const [modalAgent, setModalAgent] = useState<AgentRow | null>(null);
  const [modalLeads, setModalLeads] = useState<Lead[]>([]);
  const [modalTotal, setModalTotal] = useState(0);
  const [modalPage, setModalPage] = useState(1);
  const [modalLoading, setModalLoading] = useState(false);

  useEffect(() => {
    if (!l1) {
      setAgents([]);
      setTotalLeads(0);
      return;
    }
    fetchAgents();
  }, [fromDate, toDate, leadBucket, l1, l2, l3, lastAbcDateFrom, lastAbcDateTo, lastCallStatus, lastCallDurationMin]);

  const fetchAgents = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await axiosInstance.post("/reports/disposition/agent-breakdown", {
        from_date: fromDate,
        to_date: toDate,
        lead_bucket: leadBucket || null,
        l1_option_id: l1,
        l2_option_id: l2 || null,
        l3_option_id: l3 || null,
        last_abc_date_from: lastAbcDateFrom || null,
        last_abc_date_to: lastAbcDateTo || null,
        last_call_status: lastCallStatus || null,
        last_call_duration_min: lastCallDurationMin || null,
      });
      if (res.data.success) {
        setAgents(res.data.data.agents);
        setTotalLeads(res.data.data.total_leads);
        setDispositionLabel(res.data.data.disposition_label);
      } else {
        setError("Failed to load data");
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || "Error loading data");
    } finally {
      setLoading(false);
    }
  };

  const openModal = async (agent: AgentRow) => {
    setModalAgent(agent);
    setModalLeads([]);
    setModalPage(1);
    setModalTotal(0);
    await fetchLeads(agent.user_id, 1, []);
  };

  const fetchLeads = async (userId: number, page: number, existing: Lead[]) => {
    try {
      setModalLoading(true);
      const res = await axiosInstance.post("/reports/disposition/agent-leads", {
        from_date: fromDate,
        to_date: toDate,
        lead_bucket: leadBucket || null,
        l1_option_id: l1,
        l2_option_id: l2 || null,
        l3_option_id: l3 || null,
        agent_user_id: userId,
        page,
        limit: 20,
      });
      if (res.data.success) {
        setModalLeads([...existing, ...res.data.data.leads]);
        setModalTotal(res.data.data.total);
        setModalPage(page);
      }
    } catch {
      // silent — modal shows whatever loaded
    } finally {
      setModalLoading(false);
    }
  };

  const loadMore = () => {
    if (!modalAgent) return;
    fetchLeads(modalAgent.user_id, modalPage + 1, modalLeads);
  };

  const closeModal = () => {
    setModalAgent(null);
    setModalLeads([]);
  };

  // ── Empty state ──
  if (!l1) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center">
        <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4">
          <ChevronDown className="w-8 h-8 text-gray-400" />
        </div>
        <p className="text-gray-600 font-medium text-lg">Select a Lead Status above</p>
        <p className="text-gray-400 text-sm mt-1">Choose L1 to see the agent breakdown</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 mx-auto mb-3" style={{ borderColor: BRAND_COLOR }} />
          <p className="text-gray-500 text-sm">Loading agents...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center justify-between">
        <p className="text-red-700 text-sm">{error}</p>
        <button onClick={fetchAgents} className="text-sm px-3 py-1.5 bg-red-600 text-white rounded hover:bg-red-700">
          Retry
        </button>
      </div>
    );
  }

  if (agents.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <p className="text-gray-500 font-medium">No agents found</p>
        <p className="text-gray-400 text-sm mt-1">No leads match the selected disposition and date range</p>
      </div>
    );
  }

  const maxCount = agents[0]?.lead_count || 1;

  return (
    <>
      {/* Results header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-sm text-gray-500">
            Showing results for{" "}
            <span className="font-semibold text-gray-800">{dispositionLabel}</span>
          </p>
        </div>
        <p className="text-sm text-gray-500">
          <span className="font-bold text-gray-900">{totalLeads}</span> total leads
        </p>
      </div>

      {/* Ranked table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide w-12">#</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Agent</th>
              <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide w-24">Leads</th>
              <th className="px-4 py-3 w-48"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {agents.map((agent, idx) => {
              const barWidth = Math.round((agent.lead_count / maxCount) * 100);
              return (
                <tr
                  key={agent.user_id}
                  className="transition hover:bg-gray-50 cursor-pointer"
                  onClick={() => openModal(agent)}
                >
                  <td className="px-4 py-3 text-sm font-semibold text-gray-400 w-12">
                    {idx + 1}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div
                        className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                        style={{ backgroundColor: BRAND_COLOR }}
                      >
                        {initials(agent.agent_name)}
                      </div>
                      <span className="text-sm font-medium text-gray-900">{agent.agent_name}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span className="text-lg font-bold text-gray-900">{agent.lead_count}</span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="w-full bg-gray-100 rounded-full h-2">
                      <div
                        className="h-2 rounded-full transition-all"
                        style={{ width: `${barWidth}%`, backgroundColor: BRAND_COLOR }}
                      />
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="bg-gray-50 border-t border-gray-200">
              <td colSpan={2} className="px-4 py-3 text-sm font-semibold text-gray-700">
                Total
              </td>
              <td className="px-4 py-3 text-right">
                <span className="text-lg font-bold text-gray-900">{totalLeads}</span>
              </td>
              <td />
            </tr>
          </tfoot>
        </table>
      </div>

      {/* Modal */}
      {modalAgent && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-lg max-h-[80vh] flex flex-col overflow-hidden shadow-xl">
            {/* Modal header */}
            <div className="px-6 py-4 flex items-center justify-between flex-shrink-0" style={{ backgroundColor: BRAND_COLOR }}>
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center text-white text-sm font-bold">
                  {initials(modalAgent.agent_name)}
                </div>
                <div>
                  <p className="text-white font-semibold">{modalAgent.agent_name}</p>
                  <p className="text-white/70 text-xs">
                    {modalAgent.lead_count} leads · <span className="font-medium text-white/90">{dispositionLabel}</span>
                  </p>
                </div>
              </div>
              <button onClick={closeModal} className="text-white hover:bg-white/20 p-1.5 rounded-lg transition">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Leads table */}
            <div className="flex-1 overflow-y-auto">
              <table className="w-full">
                <thead className="sticky top-0 bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Name</th>
                    <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Phone</th>
                    <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Date</th>
                    <th className="w-8" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {modalLeads.map((lead) => (
                    <tr
                      key={lead.lead_id}
                      className="hover:bg-gray-50 cursor-pointer transition"
                      onClick={() => window.open(`/lead/${lead.uuid}`, "_blank")}
                    >
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">{lead.name}</td>
                      <td className="px-4 py-3 text-sm text-gray-600 font-mono">{maskPhone(lead.phone)}</td>
                      <td className="px-4 py-3 text-sm text-gray-500">{lead.created_date}</td>
                      <td className="px-4 py-3 text-right">
                        <ExternalLink className="w-3.5 h-3.5 text-gray-400" />
                      </td>
                    </tr>
                  ))}
                  {modalLeads.length === 0 && !modalLoading && (
                    <tr>
                      <td colSpan={4} className="px-4 py-6 text-center text-gray-400 text-sm">
                        No leads found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Footer */}
            <div className="px-6 py-3 border-t border-gray-200 flex items-center justify-between flex-shrink-0 bg-gray-50">
              <p className="text-xs text-gray-500">
                Showing {modalLeads.length} of {modalTotal}
              </p>
              {modalLeads.length < modalTotal && (
                <button
                  onClick={loadMore}
                  disabled={modalLoading}
                  className="text-xs font-semibold px-3 py-1.5 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-100 transition disabled:opacity-50"
                >
                  {modalLoading ? "Loading..." : "Load more"}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};
